import {
    Component,
    input,
    output,
    signal,
    effect,
    ViewChild,
    TemplateRef,
    computed,
    model,
    OnInit,
    OnDestroy,
    forwardRef,
    inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MultiSelectModule } from 'primeng/multiselect';
import {
    MultiSelectOption,
    MultiSelectGroup,
    MultiSelectConfig,
    MultiSelectSearchEvent,
    MultiSelectLoadMoreEvent,
    MultiSelectChangeEvent,
    MultiSelectSelectAllChangeEvent,
} from './multi-select.model';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { ButtonComponent } from '../button/button.component';
import { TextCasePipe } from 'src/app/@shared/pipes/textCase.pipe';

@Component({
    selector: 'app-multi-select',
    standalone: true,
    imports: [CommonModule, FormsModule, MultiSelectModule, ButtonComponent],
    templateUrl: './multi-select.component.html',
    styleUrls: ['./multi-select.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => MultiSelectComponent),
            multi: true,
        },
        TextCasePipe
    ],
})
export class MultiSelectComponent implements OnInit, OnDestroy, ControlValueAccessor {
    // Model binding for selected value(s)
    value = model<any[]>([]);

    // Input signals for configuration
    options = input<MultiSelectOption[] | MultiSelectGroup[]>([]);
    config = input<MultiSelectConfig>({});
    optionLabel = input<string>('label');
    optionValue = input<string>('value');
    optionDisabled = input<string>('disabled');

    // Custom templates
    itemTemplate = input<TemplateRef<any> | null>(null);
    selectedItemsTemplate = input<TemplateRef<any> | null>(null);
    headerTemplate = input<TemplateRef<any> | null>(null);
    footerTemplate = input<TemplateRef<any> | null>(null);
    emptyTemplate = input<TemplateRef<any> | null>(null);
    groupTemplate = input<TemplateRef<any> | null>(null);
    chipTemplate = input<TemplateRef<any> | null>(null);
    headerCheckboxIconTemplate = input<TemplateRef<any> | null>(null);

    // Output events
    onChange = output<MultiSelectChangeEvent>();
    onSearch = output<MultiSelectSearchEvent>();
    onLoadMore = output<MultiSelectLoadMoreEvent>();
    onShow = output<void>();
    onHide = output<void>();
    onClear = output<void>();
    onSelectAllChange = output<MultiSelectSelectAllChangeEvent>();
    onFocus = output<void>();
    onBlur = output<void>();

    // Internal state
    searchQuery = signal<string>('');
    filteredOptions = signal<MultiSelectOption[] | MultiSelectGroup[]>([]);
    /**
     * Internal loading state for operations like infinite scroll
     * Combined with config.loading to provide overall loading state
     */
    internalLoading = signal<boolean>(false);
    currentPage = signal<number>(0);
    hasMore = signal<boolean>(true);
    selectAll = signal<boolean>(false);

    // PrimeNG MultiSelect reference
    @ViewChild('multiSelectComponent') multiSelectComponent: any;

    // Subjects for reactive programming
    private searchSubject = new Subject<string>();
    private destroy$ = new Subject<void>();

    private textCasePipe = inject(TextCasePipe);

    // ControlValueAccessor implementation
    private onChangeFn: (value: any) => void = () => { };
    private onTouchedFn: () => void = () => { };

    // Computed values
    multiSelectConfig = computed(() => {
        const defaultConfig: MultiSelectConfig = {
            placeholder: 'Select options',
            searchable: true,
            searchPlaceholder: 'Search...',
            backendSearch: false,
            infiniteScroll: false,
            loadMoreThreshold: 10,
            pageSize: 20,
            disabled: false,
            loading: false,
            filter: true,
            filterMatchMode: 'contains',
            showClear: true,
            virtualScroll: false,
            virtualScrollItemSize: 43,
            appendTo: null,
            maxSelectedLabels: 3,
            selectedItemsLabel: '{0} items selected',
            display: 'comma',
            showToggleAll: true,
            selectionLimit: undefined,
            group: false,
            optionGroupLabel: 'label',
            optionGroupChildren: 'items',
        };
        return { ...defaultConfig, ...this.config() };
    });

    /**
     * Computed loading state - combines config.loading and internal loading
     * This provides a single source of truth for the loading state
     * - config.loading: Controlled by parent component (e.g., when fetching initial data)
     * - internalLoading: Controlled internally (e.g., for infinite scroll load more)
     */
    isLoading = computed(() => {
        return this.multiSelectConfig().loading || this.internalLoading();
    });

    displayOptions = computed(() => {
        const config = this.multiSelectConfig();
        const opts = this.options();

        // If backend search is enabled, return options as-is
        if (config.backendSearch) {
            return opts;
        }

        // If frontend search and there's a query, filter
        if (config.searchable && !config.backendSearch && this.searchQuery()) {
            return this.filterOptionsFrontend(opts, this.searchQuery());
        }

        return opts;
    });

    constructor() {
        // Setup search debouncing
        this.searchSubject
            .pipe(
                debounceTime(300),
                distinctUntilChanged(),
                takeUntil(this.destroy$)
            )
            .subscribe((query) => {
                this.handleSearch(query);
            });

        // Effect to update filtered options when options change
        effect(() => {
            const opts = this.options();
            this.filteredOptions.set(opts);
        });
    }

    ngOnInit(): void {
        // Initialize component
        // Loading state is now handled by computed signal
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Handle value change from ngModel
     */
    onValueChange(newValue: any[]): void {
        const config = this.multiSelectConfig();
        let finalValue = newValue || [];

        // Check selection limit
        if (config.selectionLimit && finalValue.length > config.selectionLimit) {
            finalValue = finalValue.slice(0, config.selectionLimit);
        }

        // Update the internal value
        this.value.set(finalValue);

        // Notify Angular forms of the change
        this.onChangeFn(finalValue);
        this.onTouchedFn();

        // Emit the change event
        this.onChange.emit({
            value: finalValue,
            originalEvent: undefined,
        });
    }

    /**
     * Handle search input
     */
    onSearchInput(event: any): void {
        const query = event.filter || '';
        this.searchQuery.set(query);

        const config = this.multiSelectConfig();

        if (config.backendSearch) {
            // Trigger backend search via debounced subject
            this.searchSubject.next(query);
        } else {
            // Frontend filtering is handled by computed signal
            this.filteredOptions.set(this.displayOptions());
        }
    }

    /**
     * Handle backend search
     */
    private handleSearch(query: string): void {
        this.onSearch.emit({
            query,
        });
    }

    /**
     * Frontend filter implementation
     */
    private filterOptionsFrontend(
        options: MultiSelectOption[] | MultiSelectGroup[],
        query: string
    ): MultiSelectOption[] | MultiSelectGroup[] {
        if (!query) return options;

        const config = this.multiSelectConfig();
        const lowerQuery = query.toLowerCase();
        const matchMode = config.filterMatchMode || 'contains';
        const labelKey = this.optionLabel();

        // Check if options are grouped
        if (config.group && options.length > 0 && 'items' in options[0]) {
            const groupedOptions = options as MultiSelectGroup[];
            return groupedOptions
                .map((group) => ({
                    ...group,
                    items: group.items.filter((option) =>
                        this.matchesFilter(option[labelKey], lowerQuery, matchMode)
                    ),
                }))
                .filter((group) => group.items.length > 0);
        }

        // Regular options
        const regularOptions = options as MultiSelectOption[];
        return regularOptions.filter((option) =>
            this.matchesFilter(option[labelKey], lowerQuery, matchMode)
        );
    }

    /**
     * Check if label matches filter
     */
    private matchesFilter(label: string | undefined, query: string, matchMode: string): boolean {
        const lowerLabel = label?.toLowerCase() || '';

        switch (matchMode) {
            case 'startsWith':
                return lowerLabel.startsWith(query);
            case 'endsWith':
                return lowerLabel.endsWith(query);
            case 'contains':
            default:
                return lowerLabel.includes(query);
        }
    }

    /**
     * Handle infinite scroll or load more
     */
    onScroll(event: any): void {
        const config = this.multiSelectConfig();

        if (!config.infiniteScroll || !this.hasMore() || this.isLoading()) {
            return;
        }

        const element = event.target;
        const scrollTop = element.scrollTop;
        const scrollHeight = element.scrollHeight;
        const clientHeight = element.clientHeight;

        // Check if near bottom (within threshold)
        const threshold = config.loadMoreThreshold || 10;
        const isNearBottom =
            scrollTop + clientHeight >= scrollHeight - threshold * (config.virtualScrollItemSize || 43);

        if (isNearBottom) {
            this.loadMore();
        }
    }

    /**
     * Load more items
     */
    loadMore(): void {
        const config = this.multiSelectConfig();
        const currentLength = this.options().length;
        const pageSize = config.pageSize || 20;
        const nextPage = this.currentPage() + 1;

        this.currentPage.set(nextPage);
        this.internalLoading.set(true);

        this.onLoadMore.emit({
            first: currentLength,
            last: currentLength + pageSize,
        });
    }

    /**
     * Handle show event
     */
    handleShow(): void {
        this.onShow.emit();
    }

    /**
     * Handle hide event
     */
    handleHide(): void {
        this.onHide.emit();
    }

    /**
     * Handle clear event
     */
    handleClear(): void {
        this.searchQuery.set('');
        this.value.set([]);
        this.selectAll.set(false);
        this.onClear.emit();
    }

    /**
     * Handle focus event
     */
    handleFocus(): void {
        this.onTouchedFn();
        this.onFocus.emit();
    }

    /**
     * Handle blur event
     */
    handleBlur(): void {
        this.onBlur.emit();
    }

    /**
     * Handle select all change
     */
    handleSelectAllChange(event: any): void {
        const isChecked = event.checked;
        this.selectAll.set(isChecked);

        // Update the value when select all changes
        if (isChecked) {
            this.selectAllOptions();
        } else {
            this.deselectAllOptions();
        }

        this.onSelectAllChange.emit({
            checked: isChecked,
            originalEvent: event.originalEvent,
        });
    }

    /**
     * Public method to update options (useful for parent components)
     */
    updateOptions(newOptions: MultiSelectOption[] | MultiSelectGroup[]): void {
        this.filteredOptions.set(newOptions);
    }

    /**
     * Public method to set loading state
     */
    setLoading(loading: boolean): void {
        this.internalLoading.set(loading);
    }

    /**
     * Public method to set hasMore state for infinite scroll
     */
    setHasMore(hasMore: boolean): void {
        this.hasMore.set(hasMore);
    }

    /**
     * Public method to reset pagination
     */
    resetPagination(): void {
        this.currentPage.set(0);
        this.hasMore.set(true);
    }

    /**
     * Public method to clear search
     */
    clearSearch(): void {
        this.searchQuery.set('');
    }

    /**
     * Public method to select all options
     */
    selectAllOptions(): void {
        const config = this.multiSelectConfig();
        let allValues: any[] = [];

        if (config.group) {
            const groupedOptions = this.displayOptions() as MultiSelectGroup[];
            allValues = groupedOptions.flatMap((group) =>
                group.items
                    .filter((option) => !option.disabled)
                    .map((option) => {
                        const valueKey = this.optionValue();
                        return option[valueKey] !== undefined ? option[valueKey] : option;
                    })
            );
        } else {
            const regularOptions = this.displayOptions() as MultiSelectOption[];
            allValues = regularOptions
                .filter((option) => !option.disabled)
                .map((option) => {
                    const valueKey = this.optionValue();
                    return option[valueKey] !== undefined ? option[valueKey] : option;
                });
        }

        this.value.set(allValues);
        this.selectAll.set(true);

        // Notify forms
        this.onChangeFn(allValues);
        this.onChange.emit({
            value: allValues,
            originalEvent: undefined,
        });
    }

    /**
     * Public method to deselect all options
     */
    deselectAllOptions(): void {
        this.value.set([]);
        this.selectAll.set(false);

        // Notify forms
        this.onChangeFn([]);
        this.onChange.emit({
            value: [],
            originalEvent: undefined,
        });
    }

    /**
     * Check if option is disabled
     */
    isOptionDisabled(option: MultiSelectOption): boolean {
        return option.disabled || false;
    }

    // ControlValueAccessor methods
    /**
     * Write a new value to the element (called by Angular forms)
     */
    writeValue(value: any): void {
        const newValue = Array.isArray(value) ? value : (value ? [value] : []);
        this.value.set(newValue);
    }

    /**
     * Register a callback function to be called when the control's value changes
     */
    registerOnChange(fn: (value: any) => void): void {
        this.onChangeFn = fn;
    }

    /**
     * Register a callback function to be called when the control is touched
     */
    registerOnTouched(fn: () => void): void {
        this.onTouchedFn = fn;
    }

    /**
     * Set the disabled state of the control
     */
    setDisabledState(isDisabled: boolean): void {
        // Note: The disabled state should be managed through the config input
        // This method is required by ControlValueAccessor interface
        // The parent component should update the config.disabled property
    }

    getPlaceholderText(): string {
        return this.multiSelectConfig().placeholder ? this.textCasePipe.transform(this.multiSelectConfig().placeholder || '') : '';
    }
}

